import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './QuestCarousel.module.css';
import { QuestCard } from './QuestCard';
import { QuestGroup } from '../../types';

interface QuestCarouselProps {
  quests: QuestGroup[];
}

export const QuestCarousel: React.FC<QuestCarouselProps> = ({ quests }) => {
  const navigate = useNavigate();
  const carouselRef = useRef<HTMLDivElement>(null);
  const activeQuestRef = useRef<HTMLDivElement>(null);

  const handleQuestClick = (questId: number) => {
    console.log(`Clicked on quest with ID: ${questId}`);
    
    // Находим квест по ID
    const quest = quests.find(q => q.id === questId);
    
    if (!quest) {
      console.error(`Quest with ID ${questId} not found`);
      return;
    }
    
    // Перенаправляем на страницу квестов
    navigate(`/quests/${quest.slug}`, {
      state: { groupId: quest.id }
    });
  };

  // Находим индекс активного квеста
  const activeQuestIndex = quests.findIndex(quest => quest.is_active === true);

  // Прокручиваем к активному квесту при монтировании компонента
  useEffect(() => {
    if (quests.length <= 1 || activeQuestIndex === -1) return;

    const scrollToActiveQuest = () => {
      if (carouselRef.current && activeQuestRef.current) {
        const carousel = carouselRef.current;
        const activeQuest = activeQuestRef.current;
        
        // Получаем позицию активного квеста относительно карусели
        const carouselRect = carousel.getBoundingClientRect();
        const activeQuestRect = activeQuest.getBoundingClientRect();
        
        // Вычисляем позицию для прокрутки (центрируем активный квест)
        const scrollLeft = activeQuestRect.left - carouselRect.left - 
                          (carouselRect.width / 2) + (activeQuestRect.width / 2);
        
        // Плавно прокручиваем к активному квесту
        carousel.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    };

    // Небольшая задержка для уверенности, что компоненты отрендерились
    const timer = setTimeout(scrollToActiveQuest, 100);
    
    return () => clearTimeout(timer);
  }, [quests, activeQuestIndex]);

  if (quests.length === 0) {
    return null;
  }

  if (quests.length === 1) {
    return (
      <div className={styles.container}>
        <h3 className={styles.title}>Квесты</h3>
        <div className={styles.singleCardContainer}>
          <QuestCard 
            quest={quests[0]} 
            onClick={handleQuestClick}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Квесты</h3>
      <div className={styles.carousel} ref={carouselRef}>
        {quests.map((quest, index) => (
          <div ref={index === activeQuestIndex ? activeQuestRef : null} style={{scrollSnapAlign: 'center'}}>
            <QuestCard 
              key={quest.id} 
              quest={quest} 
              onClick={handleQuestClick}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
